/**
 * Snusify cart drawer (snippets/snusify-cart-drawer): the cart in a panel at
 * the side, opened by anything marked data-cart-drawer-open - the header's
 * cart link, and the links in the add-to-cart notice. Adding to cart never
 * opens it.
 *
 * - The panel is a native modal dialog: focus stays inside while it is open,
 *   Escape or a click beside it closes it, and focus goes back to what
 *   opened it - or to the header's cart link, when that was re-rendered or
 *   hidden in the meantime.
 * - A line's minus and plus change the cart through the cart API, asking for
 *   this drawer's section in the same request, and the answer is swapped in.
 *   Every price is the cart's own, discounts included; nothing is priced
 *   here. Focus stays on the control that was pressed, or moves to the next
 *   line when its own has gone.
 * - The quantity can be typed as well. It takes effect on Enter or when
 *   focus leaves the field, kept to the line's steps and stock; 0 takes the
 *   line out, and Escape or an empty field puts the quantity back.
 * - Every other cart change on the page reports through the cart:item-added
 *   and snusify:cart-changed events. When the script behind it asked for this
 *   drawer's section too, the drawer keeps up without a request of its own.
 *   Opening it checks once more, for a cart changed elsewhere - in another
 *   tab, or before the page came back from the back-forward cache.
 * - Shopify's quick checkout (PayPal) sits under the swapped part and is
 *   left as it is while the cart it was drawn for stays the same, since a
 *   fresh copy reloads PayPal's frame.
 */

const DRAWER_SECTION = 'snusify-cart-drawer';
const CART_LINK_SECTION = 'snusify-cart-link';
const CART_STATE_SECTION = 'snusify-cart-state';

/**
 * @typedef {Object} FocusMark - finds a control again once the lines are new
 * @property {string} [key] - the line's key
 * @property {string} [action] - the control's job on the line
 * @property {string} [focus] - the data-drawer-focus of a control that is not on a line
 * @property {number} index - which line it was on
 */

class SnusifyCartDrawer extends HTMLElement {
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused. */
  #abort = null;
  #busy = false;
  /** @type {Element | null} */
  #returnTo = null;

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('[data-drawer-dialog]');
  }

  /** @returns {HTMLElement | null} */
  get #content() {
    return this.querySelector('[data-drawer-content]');
  }

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const dialog = this.#dialog;
    if (!dialog) return;

    document.addEventListener('click', (event) => this.#onTrigger(event), { signal });
    // The panel fills the dialog, so a click that lands on the dialog itself
    // was on the backdrop beside it.
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.close();
      },
      { signal }
    );
    dialog.addEventListener('close', () => this.#restoreFocus(), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-line-input]')) {
          this.#commit(event.target);
        }
      },
      { signal }
    );
    this.addEventListener('keydown', (event) => this.#onFieldKey(event), { signal });

    document.addEventListener('cart:item-added', (event) => this.#apply(event.detail?.sections), { signal });
    document.addEventListener(
      'snusify:cart-changed',
      (event) => {
        if (event.detail?.source !== this) this.#apply(event.detail?.sections);
      },
      { signal }
    );
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#refresh();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {Element | null} [trigger] - where focus goes back to on close
   */
  open(trigger = null) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    this.#returnTo = trigger ?? document.activeElement;
    dialog.showModal();
    document.dispatchEvent(new CustomEvent('snusify:cart-drawer-open'));
    this.#refresh();
  }

  close() {
    this.#dialog?.close();
  }

  /**
   * A plain click on a drawer link opens the drawer instead of the cart
   * page; a click meant for a new tab or window still goes there.
   * @param {MouseEvent} event
   */
  #onTrigger(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-cart-drawer-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-drawer-close]')) {
      this.close();
      return;
    }
    const step = target?.closest('[data-line-quantity]');
    if (step instanceof HTMLButtonElement && !step.disabled) {
      this.#change(step, Number(step.dataset.lineQuantity), this.#markOf(step));
    }
  }

  /**
   * Enter in a quantity field sets it; Escape puts the line's quantity back
   * before the drawer closes, so the blur that follows changes nothing.
   * @param {KeyboardEvent} event
   */
  #onFieldKey(event) {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || !field.matches('[data-line-input]')) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#commit(field);
    } else if (event.key === 'Escape') {
      field.value = field.dataset.lineCurrent ?? field.value;
    }
  }

  /**
   * Set a line to the quantity typed into its field, kept to the line's
   * rules: whole cans, in its steps, at least its minimum and at most its
   * stock, or 0, which takes the line out. Anything unreadable puts the
   * line's quantity back.
   * @param {HTMLInputElement} field
   */
  #commit(field) {
    const current = Number(field.dataset.lineCurrent);
    const typed = Number.parseInt(field.value, 10);
    if (!Number.isFinite(typed) || typed < 0) {
      field.value = String(current);
      return;
    }

    let quantity = 0;
    if (typed > 0) {
      const step = Number(field.step) || 1;
      const least = Number(field.dataset.lineLeast) || step;
      quantity = Math.max(least, Math.round(typed / step) * step);
      // A line whose stock has run out is left to the cart to refuse, with
      // its own message, rather than taken out here.
      const most = field.max === '' ? Infinity : Math.floor(Number(field.max) / step) * step;
      if (most >= least) quantity = Math.min(quantity, most);
    }
    field.value = String(quantity);
    // Focus is wherever the visitor has taken it by the time the cart
    // answers - still the field after Enter, the next control after Tab.
    if (quantity !== current) this.#change(field, quantity, null);
  }

  /**
   * Set a line to a quantity.
   * @param {HTMLElement} control - the button or field it comes from, which
   *   carries the line's key
   * @param {number} quantity
   * @param {FocusMark | null} mark - the control to keep focus on, or null
   *   for wherever focus is once the cart has answered
   */
  async #change(control, quantity, mark) {
    if (this.#busy) return;
    this.#busy = true;
    this.#content?.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      const response = await fetch(`${window.routes?.cart_change_url ?? '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: control.dataset.lineKey,
          quantity,
          sections: [DRAWER_SECTION, CART_LINK_SECTION, CART_STATE_SECTION].join(','),
          sections_url: window.location.pathname + window.location.search,
        }),
        signal: this.#abort?.signal,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || '');

      this.#apply(data.sections, mark);
      this.#announce();
      // The header's cart link, every card's "in your cart" line and the
      // free-shipping progress follow from the same sections.
      this.dispatchEvent(
        new CustomEvent('snusify:cart-changed', { bubbles: true, detail: { sections: data.sections, source: this } })
      );
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#showError(error?.message || this.#dialog?.dataset.error || '');
      // The cart is as it was, so a typed field shows its quantity again.
      if (control instanceof HTMLInputElement) control.value = control.dataset.lineCurrent ?? control.value;
    } finally {
      this.#busy = false;
      this.#content?.removeAttribute('aria-busy');
    }
  }

  /** Ask the Section Rendering API for the drawer as the cart now is. */
  async #refresh() {
    try {
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('sections', DRAWER_SECTION);
      const response = await fetch(url, { signal: this.#abort?.signal });
      if (!response.ok) return;
      this.#apply(await response.json());
    } catch {
      // The drawer keeps what the page was rendered with.
    }
  }

  /**
   * Swap in the lines, the total and the count from a set of rendered
   * sections, when the drawer is among them and has changed.
   * @param {Record<string, string> | undefined} sections
   * @param {FocusMark | null} [mark] - the control to keep focus on
   */
  #apply(sections, mark = null) {
    const html = sections?.[DRAWER_SECTION];
    const content = this.#content;
    if (!html || !content) return;

    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const freshContent = fresh.querySelector('[data-drawer-content]');
    if (!freshContent) return;

    const count = this.querySelector('[data-drawer-count]');
    const freshCount = fresh.querySelector('[data-drawer-count]');
    if (count && freshCount) count.innerHTML = freshCount.innerHTML;
    this.#syncWallets(fresh);

    // A can already showing goes in marked as shown (assets/snusify-can.js),
    // so it does not fade out and back in - the focus moved back below reads
    // styles before any observer could mark it - and an unchanged drawer
    // compares equal.
    const showing = new Set(
      [...content.querySelectorAll('.snusify-can.is-in .snusify-can__image')].map((img) => img.getAttribute('src'))
    );
    for (const img of freshContent.querySelectorAll('.snusify-can__image')) {
      if (showing.has(img.getAttribute('src'))) img.closest('.snusify-can')?.classList.add('is-in');
    }

    if (content.innerHTML === freshContent.innerHTML) return;
    const active = document.activeElement;
    const focusMark = mark ?? (active instanceof HTMLElement && content.contains(active) ? this.#markOf(active) : null);
    content.innerHTML = freshContent.innerHTML;
    if (focusMark && this.#dialog?.open) this.#refocus(focusMark);
  }

  /**
   * Shopify's quick checkout follows the cart: shown while it has lines, and
   * swapped for the fresh copy only when Shopify draws that differently, as
   * for a cart that has just been created. The buttons are compared by the
   * attributes Shopify prints on them, ignoring any they add once drawn.
   * @param {Document} fresh
   */
  #syncWallets(fresh) {
    const wallets = this.querySelector('[data-drawer-wallets]');
    const freshWallets = fresh.querySelector('[data-drawer-wallets]');
    if (!(wallets instanceof HTMLElement) || !(freshWallets instanceof HTMLElement)) return;
    wallets.hidden = freshWallets.hidden;

    const buttons = wallets.querySelector('shopify-accelerated-checkout-cart');
    const freshButtons = freshWallets.querySelector('shopify-accelerated-checkout-cart');
    const same =
      buttons && freshButtons
        ? freshButtons.getAttributeNames().every((name) => buttons.getAttribute(name) === freshButtons.getAttribute(name))
        : wallets.innerHTML === freshWallets.innerHTML;
    if (!same) wallets.innerHTML = freshWallets.innerHTML;
  }

  /**
   * @param {HTMLElement} control
   * @returns {FocusMark}
   */
  #markOf(control) {
    const line = control.closest('[data-line]');
    const lines = [...(this.#content?.querySelectorAll('[data-line]') ?? [])];
    return {
      key: control.dataset.lineKey,
      action: control.dataset.lineAction,
      focus: control.dataset.drawerFocus,
      index: line ? lines.indexOf(line) : -1,
    };
  }

  /**
   * Focus the same control on the new lines. A control that is now disabled
   * gives way to the other step on its line; a line that has gone, to the
   * line now in its place, then to whatever the drawer still offers.
   * @param {FocusMark} mark
   */
  #refocus(mark) {
    const content = this.#content;
    if (!content) return;

    /** @type {Element | null} */
    let target = null;
    if (mark.key && mark.action) {
      target = content.querySelector(
        `[data-line-key="${CSS.escape(mark.key)}"][data-line-action="${CSS.escape(mark.action)}"]`
      );
      if (target instanceof HTMLButtonElement && target.disabled) {
        target = target.closest('[data-line]')?.querySelector('[data-line-quantity]:not(:disabled)') ?? null;
      }
    } else if (mark.focus) {
      target = content.querySelector(`[data-drawer-focus="${CSS.escape(mark.focus)}"]`);
    }

    if (!target && mark.index >= 0) {
      // The line now in its place, whose key may differ: the same control
      // on it, then any step still enabled.
      const lines = [...content.querySelectorAll('[data-line]')];
      const line = lines[Math.min(mark.index, lines.length - 1)];
      const same = mark.action ? `[data-line-action="${CSS.escape(mark.action)}"]:not(:disabled)` : null;
      target = (same ? line?.querySelector(same) : null) ?? line?.querySelector('[data-line-quantity]:not(:disabled)') ?? null;
    }
    target ??= content.querySelector('[data-drawer-focus]') ?? this.querySelector('[data-drawer-close]');
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * Say the new total - or that the cart is empty - once a change is in.
   * Clear then set, so the same words twice are still announced.
   */
  #announce() {
    const status = this.querySelector('[data-drawer-status]');
    const dialog = this.#dialog;
    if (!status || !dialog) return;
    const total = this.querySelector('[data-drawer-total]')?.textContent?.trim();
    const message = total ? `${dialog.dataset.totalLabel ?? ''}: ${total}` : dialog.dataset.emptyLabel ?? '';
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /** @param {string} message - nothing hides the line */
  #showError(message) {
    const error = this.querySelector('[data-drawer-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #restoreFocus() {
    const back = this.#returnTo;
    this.#returnTo = null;
    if (back instanceof HTMLElement && back.isConnected && back.checkVisibility?.() !== false) {
      if (document.activeElement !== back) back.focus({ preventScroll: true });
      return;
    }
    const link = document.querySelector('[data-snusify-cart-link]');
    if (link instanceof HTMLElement) link.focus({ preventScroll: true });
  }
}

if (!customElements.get('snusify-cart-drawer')) {
  customElements.define('snusify-cart-drawer', SnusifyCartDrawer);
}
